import { AfterViewInit, ElementRef, EventEmitter, inject, Inject, Injectable, Injector, OnDestroy, OnInit } from "@angular/core";
import { EventManager } from "@angular/platform-browser";
import { addClass,  isDefined, removeAttr, removeClass, runtimeEventManager } from "@wm/core";
import { extend, forEach, get, includes, isArray, isNull, isString, keys, map, pick, set, zipObject } from "lodash-es";
import { DISPLAY_TYPE} from "../../framework/constants";
import { Parent } from "../../framework/parent.provider";
import { ChangeListener, Context, IWidgetConfig, WidgetConfig } from "../../framework/types";
import { widgetIdGenerator } from "../../framework/widget-id-generator";
import { getWidgetPropsByType } from "../../framework/widget-props";
import { WidgetProxyProvider } from "../../framework/widget-proxy.provider";
import { getWidgetRegistry, register } from "../../framework/widget-registry";
import { ReplaySubject, Subject } from "rxjs";

const DB_CONSTANTS: any = {
    "COLUMN_VALUE_TYPES":{
    "user-defined": "User Defined",
    "server-defined": "Server Defined",
    "database-defined": "Database Defined",
    "app-environment-defined": "App Environment Defined"
  },
  "COLUMN_SERVER_PROPERTIES":{
    "DATE": "Current Date",
    "TIME": "Current Time",
    "TIMESTAMP": "Current Timestamp",
    "DATE_TIME": "Current Datetime",
    "USER_ID": "LoggedIn UserId",
    "USER_NAME": "LoggedIn Username"
  },
}

  @Injectable()
  export abstract class DBBaseComponent implements OnInit, AfterViewInit, OnDestroy {


    // DOM node reference of the component root
    protected readonly nativeElement: HTMLElement;
    // Type of the component
    public readonly widgetType: string;
    // Most of the cases it is same as widgetType
    // for specific widgets like charts widgetType can be wm-chart whereas the subtype can be wm-bar-chart
    protected readonly widgetSubType: string;
    // Proxy for the component instance.
    public widget: any;
    // View parent component
    // eg: Page, Partial, Prefab
    // protected viewParent: any;
    // To add/remove events
    public  eventManager: EventManager;
    // context of the widget
    // when the widget is preset inside a repeater this context will have the repeater related properties
    public context: any;
    public widgetProps: { [key: string]: any };
    // Display type of the component. eg, block(Default), inline-block, inline etc
    private readonly displayType: string;
    // Property change subject and observable
    private readonly propertyChange = new Subject();
    /**
     * Internal flag to determine whether to wait for the widget initialization or not
     * If the initPromise is provided in the construction wait till the promise is resolved
     * If the initPromise is not provided proceed with the initialization, which is the default behavior.
     */
    private readonly delayedInit: boolean;

    private supportedDatatypes = ['date', 'time', 'datetime', 'string', 'integer', 'short', 'long', 'double', 'float', 'byte', 'timestamp']
     
    /**
     * Initial state of the widget.
     * Will be undefined once the initial properties are set on to the component
     */
    private initState: Map<string, any> | undefined;
    public $attrs = new Map<string, string>();
    // On Ready State change subject and observable
    private readonly readyState = new Subject();
    //Style change subject and observable
    private readonly styleChange = new ReplaySubject();
    // Component destroy subject and observable
    private readonly destroy = new Subject();
    // Map of event handler callbacks
    private eventHandlers = new Map<string, { callback: Function, locals: any }>();
    private isMuted = false;
    private _initialized = false;
  
    public eventEmitter = new EventEmitter();
    /**
     * Holds the event registration functions.
     * these functions needs to be executed after onViewInit
     */
    private toBeSetupEventsQueue: Array<Function> = [];
    public locale: any;
    EVENT: any = 'event';
    public i18nService: any;
    private pageName: string = '';
    protected constructor(
      protected inj: Injector,
      @Inject(WidgetConfig) config: IWidgetConfig,
      public viewParent?: Parent,
      initPromise?: Promise<any>
    ) {
      const elementRef = inject(ElementRef);
      this.nativeElement = elementRef.nativeElement;
      this.widgetType = config.widgetType || this.nativeElement.getAttribute('widgettype') || '';
      this.widgetSubType = config.widgetSubType || config.widgetType;
      if (this.nativeElement?.parentElement?.hasAttribute('data-col-identifier') && !this.nativeElement.getAttribute('name')) {
        this.nativeElement.setAttribute('name', this.widgetType.replace('wm-', ''));
      }
      this.displayType = config.displayType || DISPLAY_TYPE.BLOCK;
      this.context = this; 
      this.widget = this.createProxy();
      this.eventManager = inject(EventManager);
  
      (this.nativeElement as any).widget = this.widget;
      this.i18nService = runtimeEventManager.getService('i18Services');
      this.locale = this.i18nService.getMessages();
  
      this.initContext();
  
      if (config.hostClass) {
        addClass(this.nativeElement, config.hostClass);
      }
  
      // register default property change handler and style change handler
      this.registerPropertyChangeListener(this.onPropertyChange, this);
      this.registerPropertyChangeListener(this.delayedTask, this);
      this.registerReadyStateListener(() => {
        this._initialized = true;
      });
  
      // if the initPromise is provided, wait till the promise is resolved to proceed with the widget initialization
      if (!initPromise) {
        this.initWidget();
      } else {
        this.delayedInit = true;
        initPromise.then(() => {
          this.initWidget();
          this.setInitProps();
        });
      }
    }
  
    get appLocale() {
      return this.i18nService.getMessages() || this.i18nService.appLocale;
    }
  
    public getAttr(attrName: any): string | undefined {
      return this.$attrs.get(attrName);
    }
  
    get widgetId() {
      return this.nativeElement.getAttribute('widgetid');
    }
  
    protected createProxy(): any {
      return WidgetProxyProvider.create(this, this.widgetSubType, getWidgetPropsByType(this.widgetSubType));
    }
  
    /**
     * jQuery nativeElement reference of the component root
     */
    public get $element(): any {
      const el = $(this.nativeElement);
      return el;
    }
  
    public getNativeElement(): HTMLElement {
      return this.nativeElement;
    }
  
    public getWidgetType(): string {
      return this.widgetType;
    }
  
    public getWidgetSubType(): string {
      return this.widgetSubType;
    }
  
    /**
     * Update the initState with the default property values and the values provided in the markup
     * Process the attributes
     * Register the widget
     */
    protected initWidget() {
      this.initState = new Map<string, any>();
  
      // get the widget properties
      let widgetProps: { [key: string]: any } = getWidgetPropsByType(this.widgetSubType);
      if (this.widget.widgetProps) {
        // this occurs when widget has been modified and compiled and after appending new compiled element, widgetProps are lost.
        widgetProps = extend(widgetProps, this.widget.widgetProps);
      }
    
      forEach(widgetProps, (v: any, k: string) => {
        if (v && isDefined(v.value)) {
          this.initState?.set(k, v.value);
        } 
      });
    
      this.widgetProps = widgetProps;
      this.processAttrs();
  
      this.registerWidget(this.initState.get('widgettype'));
    }
  
  
    // This method adds readonly to the input widgets on canvas.
    protected processWidget() {
      let $el = $(this.nativeElement);
      let isInput = $el.is('input, textarea, select');
      let isButton = $el.is('button');
      let isInputWrapper = $el.is('wm-input, wm-textarea, wm-select, div[wmcolorpicker]');
  
      if (isInput || isInputWrapper) {
        $el.attr('readonly', 'true');
        $el.removeAttr('disabled');
      }
  
      if (isButton) {
        $el.removeAttr('disabled');
      }
    }
  
    protected initContext() {
      const context = this.context;
  
      const parentContexts = this.inj.get(Context, {});
  
      // assign the context property accordingly
      if (this.viewParent !== context) {
        this.context = context;
      } else {
        this.context = {};
      }
  
      if (parentContexts) {
        let parentContextObj: any = {};
        if (isArray(parentContexts)) {
          forEach(parentContexts, (contextObj) => {
            Object.assign(parentContextObj, contextObj);
          });
        } else {
          parentContextObj = parentContexts;
        }
        this.context = Object.assign({}, parentContextObj, this.context);
      }
    }
  
    // Process the attributes
    private processAttrs() {
      const attrs = this.nativeElement.attributes;
      let i = 0;
      map(attrs, (attr: Attr) => {
        this.$attrs.set(attr.name, attr.value);
        this.processAttr(attr.name, attr.value);
      });
    }
  
    /**
     * Process the attribute
     * If the attribute is an event expression, generate a functional representation of the expression
     *      and keep in eventHandlers
     * If the attribute is a bound expression, register a watch on the expression
     */
    protected processAttr(attrName: string, attrValue: string) {
      const { 0: propName, 1: type, 2: meta, length } = attrName.split('.');
       if (length === 1) {
        // remove class and name attributes. Component will set them on the proper node
        if (attrName === 'class') {
          removeClass(this.nativeElement, attrValue);
        } else if (attrName === 'tabindex') {
            removeAttr(this.nativeElement, attrName);
        }
  
        this.initState?.set(propName, attrValue);
      }
    }
  
  
    /**
     * Register the widget with the widgetRegistry
     */
    protected registerWidget(widgetName: string) {
      // if (this.widgetId) {
        this.registerDestroyListener(register(this.widget, this.viewParent, this.pageName, this.widgetId, widgetName));
      // }
    }
  
    public registerStyleChangeListener(fn: ChangeListener, ctx?: any) {
      if (ctx) {
        fn = fn.bind(ctx);
      }
      // @ts-ignore
      this.styleChange.subscribe(({ key, nv, ov }) => fn(key, nv, ov));
    }
  
    /**
     * Handles the common functionality across the components
     * eg,
     *  1. value of the class property will be applied on the host element
     *  2. based on the value of show property component is shown/hidden
     *
     * @param {string} key
     * @param nv
     * @param ov
     */

    protected delayedTask(){
      if(this.widgetType === 'wm-db-table-column'){
      let SQL_TYPES:any = {},
      scope = this.widget, obj, javaTypes:any = {}
      Object.keys(scope.dataModelDesignManager.typesMap[scope.dbService.selectedDB])
      .forEach(function (k) {
          // update the display value
          SQL_TYPES[k] = (k === 'unknown_type') ? 'unknown' : k;
      });
      set(this.widgetProps ,'sqltype.options', SQL_TYPES);

      if (scope.javatype) {
        if (scope.sqltype === 'number') {
            obj = scope.dataModelDesignManager.getJavaTypes(scope.sqltype, scope.precision, scope.scale, scope.dbService.selectedDB);
        } else {
            obj = scope.dataModelDesignManager.getJavaTypes(scope.sqltype, scope.length, undefined, scope.dbService.selectedDB);
        }
        if (obj) {
            javaTypes               =  zipObject(obj.alternateJavaTypes, obj.alternateJavaTypes);
            javaTypes[obj.javaType] = obj.javaType;
        }
        set(this.widgetProps, 'javatype.options', javaTypes);
    } else {
      set(this.widgetProps, 'javatype.options', obj);
      set(this.widgetProps, 'javatype.disabled', true);
    }
    let generateTypeOption:any = scope.dataModelDesignManager.getGenerators(scope.dbService.selectedDB, scope.javatype)
    set(this.widgetProps, 'generatortype.options', generateTypeOption);

  }
    }

    protected onPropertyChange(key: string, newVal: any, oldVal?: any) {
      let scope = this.widget, generators:any, options
      if(this.widgetType === 'wm-db-table-column'){
        switch (key) {
          case 'sqltype':
              if (scope.sqltype === 'number') {
                  set(this.widgetProps, 'precision.show', true);
                  set(this.widgetProps, 'scale.show', true);
                  set(this.widgetProps, 'length.show', false);
              } else if (scope.sqltype === 'string') {
                  set(this.widgetProps, 'precision.show', false);
                  set(this.widgetProps, 'scale.show', false);
                  set(this.widgetProps, 'length.show', true);
              } else {
                  set(this.widgetProps, 'precision.show', false);
                  set(this.widgetProps, 'scale.show', false);
                  set(this.widgetProps, 'length.show', false);
              }
              scope.checkGeneratorType(newVal, oldVal, ["number"], "sqltype");
              break;
          case 'generatortype':
              /*Invoke the function to modify the "sqlType" based on the "generatortype".*/
              scope.modifySqlType(oldVal);
              set(this.widgetProps, 'generatorvalue.show', scope.generatortype === 'sequence');
              scope.generatorvalue = (scope.generatorvalue && scope.generatorvalue !== 'null') ? scope.generatorvalue : '';
              break;
          case 'ispartofpk':
              /*Disable the "nullable" & "defaultValue" properties for primary key column.*/

              generators = keys(get(this.widgetProps ,'generatortype.options'));
              if (scope.isPrimaryKey(scope.ispk)) {
                  set(this.widgetProps, 'nullable.disabled', true);

                  scope.defaultvalue = scope.defaultvalue || "";
                  set(this.widgetProps, 'generatortype.show', true);

                  scope.generatortype = scope.generatortype || scope.dataModelDesignManager.getDefaultGenerator(scope.dbService.selectedDB, scope.javatype);
                  scope.checkPrimaryKeyLength();
              } else if (scope.isPartOfPrimaryKey()) {
                  set(this.widgetProps, 'nullable.disabled', true);
                  set(this.widgetProps, 'generatortype.show', true);
                  scope.generatortype = scope.generatortype || scope.dataModelDesignManager.getDefaultGenerator(scope.dbService.selectedDB, scope.javatype);
              } else {
                  set(this.widgetProps, 'nullable.disabled', false);
                  set(this.widgetProps, 'defaultvalue.disabled', false);
                  set(this.widgetProps, 'generatortype.show', false);
                  scope.generatortype = null;
              }
              // scope.updateClasses();
              break;
          case 'length':
              if (scope.length < 0) {
                  scope.length = 0;
              }
              if (scope.isPrimaryKey()) {
                  scope.checkPrimaryKeyLength();
              }
              break;
          case 'precision':
              if (scope.precision < 0) {
                  scope.precision = 0;
              }
              break;
          case 'scale':
              if (scope.scale < 0) {
                  scope.scale = 0;
              }
              break;
          case 'javatype':
              scope.valuesArray = this.getValues(this.nativeElement);
              //set server properties of javatype is changed and valuetype is set to server-defined.
              if (scope.valuetype === 'server-defined') {
                  set(this.widgetProps, 'valuetype.value' ,scope.valuetype)
                  this.setServerProperties(scope, this.widgetProps);
              }
              if (includes(this.supportedDatatypes, newVal) && !scope.ispk) {
                  set(this.widgetProps, 'valuetype.options' ,DB_CONSTANTS.COLUMN_VALUE_TYPES)
              } else {
                set(this.widgetProps, 'valuetype.options' , pick(DB_CONSTANTS.COLUMN_VALUE_TYPES, ['user-defined', 'database-defined']))
              }
              options = keys(get(this.widgetProps, 'valuetype.options'));
              scope.valuetype = includes(options, scope.valuetype) ? scope.valuetype : options[0];
              scope.checkGeneratorType(newVal, oldVal, ["integer", "short", "big_integer"], "javatype");
              break;
          case 'valuetype':
              //show properties based on valuetype selected
              switch (newVal) {
              case 'user-defined':
                set(this.widgetProps, 'defaultvalue.show' , true)
                set(this.widgetProps, 'userinsertable.show' , true)
                set(this.widgetProps, 'userupdatable.show' , true)

                scope.defaultvalue      = isNull(scope.defaultvalue) ? '' : scope.defaultvalue;
                set(this.widgetProps, 'serverproperty.show' , false)
                set(this.widgetProps, 'appenvproperty.show' , false)

                set(this.widgetProps, 'serverinsertable.show' , false)
                set(this.widgetProps, 'serverupdatable.show' , false)

                set(this.widgetProps, 'databaseinsertable.show' , false)
                set(this.widgetProps, 'databaseupdatable.show' , false)

                set(this.widgetProps, 'appenvinsertable.show' , false)
                set(this.widgetProps, 'appenvupdatable.show' , false)

                  break;
              case 'server-defined':

                  set(this.widgetProps, 'defaultvalue.show' , false)
                  set(this.widgetProps, 'appenvproperty.show' , false)

                  set(this.widgetProps, 'serverproperty.show' , true)
                  set(this.widgetProps, 'serverinsertable.show' , true)
                  set(this.widgetProps, 'serverupdatable.show' , true)

                  set(this.widgetProps, 'userinsertable.show' , false)
                  set(this.widgetProps, 'userupdatable.show' , false)


                  set(this.widgetProps, 'databaseinsertable.show' , false)
                  set(this.widgetProps, 'databaseupdatable.show' , false)

                  set(this.widgetProps, 'appenvinsertable.show' , false)
                  set(this.widgetProps, 'appenvupdatable.show' , false)

                  this.setServerProperties(scope, this.widgetProps);
                  break;
              case 'app-environment-defined':

                  set(this.widgetProps, 'defaultvalue.show' , false)

                  set(this.widgetProps, 'serverproperty.show' , false)
                  set(this.widgetProps, 'serverinsertable.show' , false)
                  set(this.widgetProps, 'serverupdatable.show' , false)

                  set(this.widgetProps, 'appenvproperty.show' , true)

                  set(this.widgetProps, 'userinsertable.show' , false)
                  set(this.widgetProps, 'userupdatable.show' , false)


                  set(this.widgetProps, 'databaseinsertable.show' , false)
                  set(this.widgetProps, 'databaseupdatable.show' , false)

                  set(this.widgetProps, 'appenvinsertable.show' , true)
                  set(this.widgetProps, 'appenvupdatable.show' , true)

                  this.setAppEnvProperties(scope, this.widgetProps);
                  break;
              case 'database-defined':

              set(this.widgetProps, 'defaultvalue.show' , false)

                  set(this.widgetProps, 'serverproperty.show' , false)
                  set(this.widgetProps, 'serverinsertable.show' , false)
                  set(this.widgetProps, 'serverupdatable.show' , false)

                  set(this.widgetProps, 'appenvproperty.show' , false)

                  set(this.widgetProps, 'userinsertable.show' , false)
                  set(this.widgetProps, 'userupdatable.show' , false)


                  set(this.widgetProps, 'databaseinsertable.show' , true)
                  set(this.widgetProps, 'databaseupdatable.show' , true)

                  set(this.widgetProps, 'appenvinsertable.show' , false)
                  set(this.widgetProps, 'appenvupdatable.show' , false)
                
                  break;
              }
              break;
          case 'partofuniquekey':
          case 'virtualpk':
              // scope.updateClasses();
              break;
          }
      }
    }

    //Set options for server properties depending on the column javatype.
    setServerProperties(scope:any, wp:any) {
         let serverProperties  = scope.dataModelDesignManager.getColumnServerProperties(scope.dbService.selectedDB),
          properties        = keys(serverProperties);

      wp.serverproperty.options = {};
      //set server property options if the javatype of the column is satisfied against the serverproperty.
      forEach(properties, function (property) {
          if (includes(serverProperties[property], scope.javatype)) {
              if (scope.javatype === 'string') {
                  wp.serverproperty.options.USER_ID   = DB_CONSTANTS.COLUMN_SERVER_PROPERTIES.USER_ID;
                  wp.serverproperty.options.USER_NAME = DB_CONSTANTS.COLUMN_SERVER_PROPERTIES.USER_NAME;
              } else if (includes(['integer', 'short', 'long', 'float', 'double', 'byte'], scope.javatype)) {
                  wp.serverproperty.options.USER_ID   = DB_CONSTANTS.COLUMN_SERVER_PROPERTIES.USER_ID;
              } else {
                  wp.serverproperty.options[property] = DB_CONSTANTS.COLUMN_SERVER_PROPERTIES[property];
              }
          }
      });
  }

     //set App Environment properties
     setAppEnvProperties(scope:any, wp:any) {
      wp.appenvproperty.options = scope.dbUtils.appEnvironmentProperties;
    }

    values:any;
    valuesArr:any;
    valuemodels='["dbobjectname", "javatype"]';

    getValues(element?:any){
      var tempArr;
      /*Case: Direct values have been passed.. Display them as is.*/
      if (this.values) {
          this.valuesArr = this.fromJson(this.values);
      } else if (this.valuemodels) {/*Case: Bound values have been passed.. Display the evaluated values*/
          /*Fetch the names of the bound values*/
          tempArr = this.fromJson(this.valuemodels);
          this.valuesArr = [];
          /*Loop through the array of bound values and evaluate them*/
          forEach(tempArr,  (value) => {
              this.valuesArr.push(this.widget[value]);
          });

      }
      if(this.valuesArr.length > 0) {
        this.widget.updateNodes(this, element);
      }
      /*Return the array with actual values*/
      return this.valuesArr;
    }

  
    /**
     * Generates a unique id
     * Default pattern is `widget-id-${id}`
     * Components can override this method to generate a different id eg, bar-chart-1
     */
    protected generateWidgetId(): string {
      return widgetIdGenerator.nextUid();
    }
  
    // Default style change handler
  
    public registerPropertyChangeListener(fn: ChangeListener, ctx?: any) {
      if (ctx) {
        fn = fn.bind(ctx);
      }
      // @ts-ignore
      this.propertyChange.subscribe(({ key, nv, ov }) => fn(key, nv, ov));
    }
  
  
    /**
     * Update the default properties and the properties provided in the markup in component
     * Invoking this method will result in invocation of propertyChange handlers on the component for the first time
     */
    protected setInitProps() {
      if (this.initState?.get('name')) {
        this.widget.name = this.initState.get('name');
      }
      this.initState?.forEach((v, k) => {
        let updateKey;
        // name is already set, ignore name
        // if the key is part of to be ignored attributes list do not set it on the component instance
        if ((this.widgetProps?.[k]) && k !== 'name') {
          if (updateKey) {
            this.widget[updateKey] = v
          }else if(k === 'sqltype'){
            this.widget[k] = this.widget.column.sqlType;
          }
          else if(k === 'javatype'){
            this.widget[k] = this.widget.column.javaType || 'string';
          }
          else if(k === 'columnidentifier'){
            this.widget[k] = this.widget.column.columnIdentifier;
          } else if(k === 'valuetype' || k ==='appenvproperty' || k ==='serverproperty' || k === 'ishidden' || k === 'virtualpk' ||  k === 'generatortype' || k === 'length') {
            this.widget[k] = this.widget.column[k] || v;
          }
           else {
            this.widget[k] = v;
          }
        }
      });
      this.initState?.clear();
      this.initState = undefined;
  
      this.readyState.next(null);
      this.readyState.complete();
    }
  
    public registerDestroyListener(fn: Function, ctx?: any) {
      if (ctx) {
        fn = fn.bind(ctx);
      }
      this.destroy.subscribe(() => { }, () => { }, () => fn());
    }
  
    /**
     * nativeElement will be available by this time
     * if the delayInit is false, properties meta will be available by this time
     * Invoke the setInitProps if the delayInit is false
     */
    ngOnInit() {
     if (!this.delayedInit) {
        this.setInitProps();
      }
    }
  
    ngAfterViewInit() {
      // When widgets rendered inside partial, which is rendered on the page, dotted border for partial widgets should not be visible
      if (this.$element.closest('.app-included-page').length) {
        const elementsWithAttr: any = this.nativeElement.querySelectorAll('[widgetid]');
        const registry = getWidgetRegistry();
        elementsWithAttr.forEach((el: any) => {
          const widgetId = el.getAttribute('widget-id');
          registry.delete(widgetId);
          el.removeAttribute('widgetid');
          el.removeAttribute('widget-id');
        });
      }
      if (this.nativeElement.hasAttribute('widget-id') || this.nativeElement.hasAttribute('widgetid')) {
        this.processWidget();
      }
    }
  
    public notifyPropertyChange(key: string, nv: any, ov: any) {
      this.propertyChange.next({ key, nv, ov });
    }
  
  
    public registerReadyStateListener(fn: Function, ctx?: any) {
      if (ctx) {
        fn = fn.bind(ctx);
      }
      if (this.readyState.isStopped) {
        fn();
        return;
      }
      this.readyState.subscribe(() => fn());
    }
  
    fromJson(json: any) {
      return isString(json)
        ? JSON.parse(json)
        : json;
    }

    ngOnDestroy() {
      this.widget = Object.create(null);
      this.styleChange.complete();
      this.propertyChange.complete();
      this.destroy.complete();
    }
  }
  